import { DatePicker } from '@progress/kendo-react-dateinputs'
import { LoadSegmentCodes } from 'components/common/load-partner-codes/component'
import { Loading, SIZES } from 'components/loading'
import { RasaContext } from 'context'
import { BaseClientEntity } from 'generic/baseClientEntity'
import { isFalsey } from 'generic/utility'
import * as React from 'react'
import { Input } from 'reactstrap'
import { DATEPICKER_DATE_FORMAT } from 'shared/constants'
import { ScheduledContentTypes, DEFAULT_COMMUNITY_PARTNER_CODES } from 'shared_server_client/constants'
import { endOfDay, startOfDay } from 'shared_server_client/dates'
import {
  ClearScheduledContentDataState,
  InitialScheduledContentModalState,
  SURVEY_MAX_OPTIONS,
  SURVEY_MIN_OPTIONS,
  SURVEY_ERROR_MESSAGE,
} from '../constants'
import { ScheduleContentModalBaseComponent, ScheduleContentModalProps } from './base'

export class ScheduledSurveyModalComponent extends ScheduleContentModalBaseComponent {
  public static key: string = 'scheduledSurveyModal'
  public static contextType = RasaContext

  constructor(props: ScheduleContentModalProps) {
    super(props, ScheduledSurveyModalComponent.key)
    this.state = {
      ...InitialScheduledContentModalState,
      surveyOptions: [{ title: '', sequence: 1, is_correct: false, is_multi_select: false, is_active: true }],
      allowMultiSelect: false
    }
  }

  public renderChildren(data) {
    return (this.state.isSaving)
    ? <Loading size={SIZES.LARGE}/>
    : <div className="scheduled-survey-modal-body">
        <div className="body-container">
          <div className="left-side-container">
            <div className="first-row-container">
              <h5>Survey Name *</h5>
              <Input
                type="text"
                value={data.name || ''}
                placeholder="Enter survey name"
                onChange={(e) => this.updateProperty('name', e.target.value)}
                maxLength={255}
              />
            </div>

            <div className="second-row-container">
              <h5>Survey Question *</h5>
              <Input
                type="textarea"
                value={data.question || ''}
                placeholder="Enter your survey question"
                onChange={(e) => this.updateProperty('question', e.target.value)}
                rows={3}
              />
            </div>

            <div className="third-row-container">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                <h5>Survey Options * (Max {SURVEY_MAX_OPTIONS})</h5>
                {this.state.surveyOptions.length < SURVEY_MAX_OPTIONS && (
                  <span
                    onClick={this.addSurveyOption}
                    title="Add option"
                    style={{fontSize: '18px', color: '#007bff', cursor: 'pointer'}}
                  >
                    <i className="fas fa-plus-circle"></i>
                  </span>
                )}
              </div>
              
              {this.state.surveyOptions.map((option, index) => (
                <div key={index} className="survey-option-row" style={{display: 'flex', alignItems: 'center', marginBottom: '8px'}}>
                  <span className="option-number" style={{minWidth: '25px', marginRight: '8px'}}>{index + 1}.</span>
                  <Input
                    type="text"
                    value={option.title}
                    placeholder={`Enter option ${index + 1}`}
                    onChange={(e) => this.updateSurveyOption(index, 'title', e.target.value)}
                    maxLength={255}
                    className="option-input"
                    style={{flex: '1 1 calc(100% - 150px)', marginRight: '8px'}}
                  />
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px', minWidth: '140px'}}>
                    <div 
                      onClick={() => this.updateSurveyOption(index, 'is_correct', !option.is_correct)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: option.is_correct ? '#28a745' : '#f8f9fa',
                        border: `1px solid ${option.is_correct ? '#28a745' : '#dee2e6'}`,
                        color: option.is_correct ? 'white' : '#6c757d',
                        fontSize: '11px',
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        minWidth: '50px',
                        justifyContent: 'center'
                      }}
                      title={option.is_correct ? "Mark as incorrect" : "Mark as correct"}
                    >
                      {option.is_correct ? (
                        <>
                          <i className="fas fa-check" style={{marginRight: '4px', fontSize: '10px'}}></i>
                          Correct
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus" style={{marginRight: '4px', fontSize: '10px'}}></i>
                          Mark as Correct
                        </>
                      )}
                    </div>
                    {this.state.surveyOptions.length > SURVEY_MIN_OPTIONS && (
                      <span
                        onClick={() => this.removeSurveyOption(index)}
                        title="Remove option"
                        style={{fontSize: '16px', color: '#dc3545', cursor: 'pointer'}}
                      >
                        <i className="fas fa-times-circle"></i>
                      </span>
                    )}
                  </div>
                </div>
              ))}
              
            </div>
            
            <div className="fourth-row-container">
              <div style={{display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '16px'}}>
                <div style={{flex: 1}}>
                  <h5 style={{marginBottom: '8px'}}>Selection Type</h5>
                  <div 
                    onClick={() => this.handleMultiSelectChange(!this.state.allowMultiSelect)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      backgroundColor: this.state.allowMultiSelect ? '#007bff' : '#f8f9fa',
                      border: `1px solid ${this.state.allowMultiSelect ? '#007bff' : '#dee2e6'}`,
                      color: this.state.allowMultiSelect ? 'white' : '#6c757d',
                      fontSize: '13px',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      justifyContent: 'center'
                    }}
                    title={this.state.allowMultiSelect ? "Switch to single selection mode" : "Switch to multiple selection mode"}
                  >
                    {this.state.allowMultiSelect ? (
                      <>
                        <i className="fas fa-check-square" style={{marginRight: '6px', fontSize: '12px'}}></i>
                        Multiple Answers
                      </>
                    ) : (
                      <>
                        <i className="far fa-square" style={{marginRight: '6px', fontSize: '12px'}}></i>
                        Single Answer
                      </>
                    )}
                  </div>
                </div>

                <div style={{flex: 1}}>
                  <h5 style={{marginBottom: '8px'}}>Results Visibility</h5>
                  <div 
                    onClick={() => this.updateProperty('hide_results', !data.hide_results)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      backgroundColor: data.hide_results ? '#dc3545' : '#28a745',
                      border: `1px solid ${data.hide_results ? '#dc3545' : '#28a745'}`,
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      justifyContent: 'center'
                    }}
                    title={data.hide_results ? "Show survey results to participants" : "Hide survey results from participants"}
                  >
                    {data.hide_results ? (
                      <>
                        <i className="fas fa-eye-slash" style={{marginRight: '6px', fontSize: '12px'}}></i>
                        Results Hidden
                      </>
                    ) : (
                      <>
                        <i className="fas fa-eye" style={{marginRight: '6px', fontSize: '12px'}}></i>
                        Results Visible
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="right-side-container">
            <div className="first-row-container" style={{marginBottom: '16px'}}>
              <h5>Success Message</h5>
              <Input
                type="textarea"
                value={data.success_message || ''}
                placeholder="Message shown after successful submission"
                onChange={(e) => this.updateProperty('success_message', e.target.value)}
                rows={2}
              />
            </div>

            <div className="second-row-container" style={{marginBottom: '16px'}}>
              <h5>Failure Message</h5>
              <Input
                type="textarea"
                value={data.fail_message || ''}
                placeholder="Message shown if submission fails"
                onChange={(e) => this.updateProperty('fail_message', e.target.value)}
                rows={2}
              />
            </div>

            <div className="third-row-container" style={{marginBottom: '16px'}}>
              <h5>Redirect URL</h5>
              <Input
                type="text"
                value={data.final_url || ''}
                placeholder="URL to redirect after survey completion"
                onChange={(e) => this.updateProperty('final_url', e.target.value)}
              />
            </div>

            <div className="fourth-row-container" style={{display: 'flex', gap: '16px', marginBottom: '16px'}}>
              <div style={{flex: 1}}>
                <h5>Start Date *</h5>
                <DatePicker
                  className="modal-schedule-date-picker no-border-radius"
                  format={DATEPICKER_DATE_FORMAT}
                  value={data.startDate ? data.startDate : null}
                  onChange={(e) => this.updateProperty('startDate', e.target.value)}
                />
              </div>
              <div style={{flex: 1}}>
                <h5>End Date *</h5>
                <DatePicker
                  className="modal-schedule-date-picker no-border-radius"
                  format={DATEPICKER_DATE_FORMAT}
                  value={data.endDate ? data.endDate : null}
                  onChange={(e) => this.updateProperty('endDate', e.target.value)}
                />
              </div>
            </div>

            <div className="fifth-row-container">
              <h5>Segment</h5>
              <LoadSegmentCodes
                label=""
                segmentCode={data.community_partner_code}
                setSegmentData={this.setSegmentData}
                setSegmentCode={this.setSegmentCode} />
            </div>
          </div>
        </div>
      </div>
  }

  public componentDidMount() {
    this.loadSurveyOptions()
  }

  public componentWillReceiveProps(nextProps: ScheduleContentModalProps) {
    // Check if modal data has changed (specifically the typeId, which indicates a different survey)
    const currentData = this.props.data[ScheduledSurveyModalComponent.key]
    const nextData = nextProps.data[ScheduledSurveyModalComponent.key]
    
    // Only reload survey options if:
    // 1. We're switching between different surveys (typeId changed)
    // 2. Modal is opening for the first time (currentData is null/undefined but nextData exists)
    // 3. Modal is closing (nextData is null/undefined but currentData exists)
    const currentTypeId = currentData && currentData.data && currentData.data.typeId
    const nextTypeId = nextData && nextData.data && nextData.data.typeId
    
    if (currentTypeId !== nextTypeId) {
      this.loadSurveyOptions(nextProps)
    }
  }

  private loadSurveyOptions(props?: ScheduleContentModalProps) {
    const propsToUse = props || this.props
    const modalData = propsToUse.data[ScheduledSurveyModalComponent.key]
    const data = modalData && modalData.data
    
    // If this is an edit, load existing data
    if (data && data.typeId) {
      const allowMultiSelect = data.is_multi_select || false
      
      // Check if survey options are already provided in the data
      if (data.survey_options && data.survey_options.length > 0) {
        const surveyOptions = data.survey_options.map((opt, index) => ({
          id: opt.id,
          title: opt.title || '',
          sequence: index + 1,
          is_correct: opt.is_correct || false,
          is_multi_select: allowMultiSelect,
          is_active: opt.is_active !== false
        }))
        
        this.setState({
          surveyOptions,
          allowMultiSelect
          // Don't set isDirty: false here - let it remain based on other field changes
        })
      } else {
        this.setState({
          allowMultiSelect
          // Don't set isDirty: false here - let it remain based on other field changes
        })
      }
    } else {
      // Reset to default state for new surveys
      this.setState({
        surveyOptions: [{ title: '', sequence: 1, is_correct: false, is_multi_select: false, is_active: true }],
        allowMultiSelect: false
      })
    }
  }

  protected getTitleText(data: any): string {
    return data.typeId
      ? 'Edit Scheduled Survey'
      : 'Add Scheduled Survey'
  }

  protected saveDisabled(data: any): boolean {
    const { name, question, endDate, startDate } = data
    const hasValidOptions = this.state.surveyOptions.length >= SURVEY_MIN_OPTIONS && 
                           !this.hasEmptyOptions()
    const isMissingData = isFalsey(name && question && endDate && startDate) || !hasValidOptions
    
    return !data.id
      ? isMissingData
      : isMissingData || !this.state.isDirty
  }

  protected xButtonClick(data: any) {
    const currentIsSuperUser = this.state.isSuperUser
    this.setState({
      ...ClearScheduledContentDataState,
      isSuperUser: currentIsSuperUser,
      surveyOptions: [{ title: '', sequence: 1, is_correct: false, is_multi_select: false, is_active: true }],
      allowMultiSelect: false
    })
    this.props.closeModal(ScheduledSurveyModalComponent.key)
  }

  protected createOrUpdate(payload: any): Promise<any> {
    return this.context.entityMetadata
      .getEntityObject('scheduledContent', payload.communityId, null)
      .then((scheduledContentEntityObject: BaseClientEntity) => {
        const { name, question, endDate, startDate, typeId, success_message, fail_message, final_url, hide_results } = payload
        
        scheduledContentEntityObject.setRecordId(payload.communityId, typeId ? typeId : null)
        scheduledContentEntityObject.data.contentType = ScheduledContentTypes.survey
        scheduledContentEntityObject.data.endDate = endOfDay(endDate, payload.timezone)
        scheduledContentEntityObject.data.startDate = startOfDay(startDate, payload.timezone)
        scheduledContentEntityObject.data.community_partner_code = payload.community_partner_code
        scheduledContentEntityObject.data.name = name
        scheduledContentEntityObject.data.question = question
        scheduledContentEntityObject.data.success_message = success_message
        scheduledContentEntityObject.data.fail_message = fail_message
        scheduledContentEntityObject.data.final_url = final_url
        scheduledContentEntityObject.data.hide_results = hide_results || false
        scheduledContentEntityObject.data.is_multi_select = this.state.allowMultiSelect || false
        scheduledContentEntityObject.data.surveyOptions = this.state.surveyOptions
        scheduledContentEntityObject.data.allowMultiSelect = this.state.allowMultiSelect || false
        scheduledContentEntityObject.data.status = 'active'
        scheduledContentEntityObject.data.is_scheduled = true
        scheduledContentEntityObject.data.is_archived = false
        scheduledContentEntityObject.data.options = JSON.stringify(this.state.surveyOptions.filter(opt => opt.title.trim()))

        if (!payload.typeId) {
          scheduledContentEntityObject.data.communityIdentifier = payload.communityId
        }

        return scheduledContentEntityObject.save()
      })
      .catch((err) => {
        this.setState({
          isSaving: false,
          scheduleErrorMessage: SURVEY_ERROR_MESSAGE,
        })
        return Promise.reject(err)
      })
  }

  private addSurveyOption = () => {
    if (this.state.surveyOptions.length < SURVEY_MAX_OPTIONS) {
      const newOptions = [...this.state.surveyOptions, {
        title: '',
        sequence: this.state.surveyOptions.length + 1,
        is_correct: false,
        is_multi_select: this.state.allowMultiSelect || false,
        is_active: true
      }]
      this.setState({ surveyOptions: newOptions, isDirty: true })
    }
  }

  private removeSurveyOption = (index: number) => {
    if (this.state.surveyOptions.length > SURVEY_MIN_OPTIONS) {
      const newOptions = this.state.surveyOptions.filter((_, i) => i !== index)
      // Update sequence numbers
      newOptions.forEach((option, i) => {
        option.sequence = i + 1
      })
      this.setState({ surveyOptions: newOptions, isDirty: true })
    }
  }

  private updateSurveyOption = (index: number, field: string, value: any) => {
    const newOptions = [...this.state.surveyOptions]
    
    // Handle correct answer logic
    if (field === 'is_correct' && value === true && !this.state.allowMultiSelect) {
      // For single select, uncheck other correct answers
      newOptions.forEach((option, i) => {
        if (i !== index) {
          option.is_correct = false
        }
      })
    }
    
    newOptions[index] = { ...newOptions[index], [field]: value }
    this.setState({ surveyOptions: newOptions, isDirty: true })
  }

  private hasEmptyOptions = (): boolean => {
    return this.state.surveyOptions.some(option => !option.title.trim())
  }

  private handleMultiSelectChange = (allowMultiSelect: boolean) => {
    const updatedOptions = this.state.surveyOptions.map((option, index) => {
      const newOption = { ...option, is_multi_select: allowMultiSelect }
      
      // If switching to single select, only keep the first correct answer
      if (!allowMultiSelect) {
        const hasCorrectBefore = this.state.surveyOptions.slice(0, index).some(opt => opt.is_correct)
        if (hasCorrectBefore && option.is_correct) {
          newOption.is_correct = false
        }
      }
      
      return newOption
    })
    
    this.setState({ 
      allowMultiSelect,
      surveyOptions: updatedOptions,
      isDirty: true 
    })
  }

  private setSegmentCode = (code: any) => {
    this.updateProperty('community_partner_code', code.key)
  }

  private setSegmentData = (configData: any) => {
    const community_partner_code = this.props.data[ScheduledSurveyModalComponent.key].data.community_partner_code
    if (!community_partner_code && configData.length > 0) {
      this.updateProperty('community_partner_code', DEFAULT_COMMUNITY_PARTNER_CODES.ALL)
    }
  }

  protected updateProperty(property, value) {
    this.props.updateModal(ScheduledSurveyModalComponent.key, {
      name: property,
      value,
    })
    this.setState({ isDirty: true })
  }
}